import { dayKeyOf, toDate } from "../event-experience/agenda-schedule";
import { eventDayCount } from "../event-experience/landing-model";
import { DAY_TITLES_MAX } from "../events/landing";

/**
 * Whether the sessions sit inside the event's dates, and what to offer when
 * they do not.
 *
 * An agenda imported into an event whose dates were never moved lands on days
 * the event does not have. The attendee side then numbers those days after the
 * event's own days (`dayNumbers`), the day titles match no day, and nothing
 * told the organiser. This is the check behind the notice on the Agenda tab
 * and in the CSV import panel.
 *
 * WHICH CLOCK. A session's day is its day on the event's clock, the same
 * `dayKeyOf(start, zone)` the schedule groups by. A session at 23:30 in Berlin
 * is that evening's session although it is already tomorrow in UTC. The event's
 * `startDate` and `endDate` are calendar dates on that same clock, so the two
 * compare as plain `YYYY-MM-DD` strings.
 *
 * THE OFFER. The dates the notice proposes are the first and last day of the
 * sessions, nothing wider and nothing narrower. It goes through the ordinary
 * event settings write, which accepts any pair of valid dates, so the only
 * limit here is ours: an event of more than `MAX_OFFERED_EVENT_DAYS` days is
 * far more likely to be a mistyped year in the file than a real event, so the
 * notice then explains and leaves the dates to the Settings tab. The limit is
 * the number of days that can have a title.
 */

export const MAX_OFFERED_EVENT_DAYS = DAY_TITLES_MAX;

export type DayRange = { first: string; last: string };

export type DatesOffer =
  | { kind: "set"; range: DayRange }
  | { kind: "too-long"; range: DayRange; days: number };

export type OutsideDates = {
  /** Sessions with a start time that can be read. */
  scheduled: number;
  /** Of those, the ones on a day the event does not have. */
  outside: number;
  /** The days they fall on, as `YYYY-MM-DD`, earliest first. */
  outsideDays: string[];
  /**
   * The "Day N" the attendee side gives each of `outsideDays`: the event's own
   * days come first, so these continue after them. Same order as `outsideDays`.
   */
  dayNumbers: number[];
  /** The event's own dates. */
  event: DayRange;
  /** What the notice proposes, from the first to the last session day. */
  offer: DatesOffer;
  /** Whether the dates in the text need a year to be told apart. */
  withYear: boolean;
};

/**
 * The sessions that are outside the event's dates, or null when there are
 * none, when the event's dates cannot be read, or when no session has a start.
 *
 * `starts` are the sessions the count is about. `alsoCover` are further
 * sessions, for example the ones already on the agenda while a file is being
 * read: they count toward the range that is offered, so the new dates do not
 * push a session that is on the agenda now outside them, but not toward the
 * count.
 */
export function outsideEventDates(
  starts: readonly unknown[],
  dates: { startDate: string; endDate: string },
  timeZone: string | undefined,
  alsoCover: readonly unknown[] = [],
): OutsideDates | null {
  // Unreadable dates leave nothing to compare against. That is the Settings
  // tab's problem, and a notice that called every session outside would only
  // hide it.
  const total = eventDayCount(dates.startDate, dates.endDate);
  if (total === null) return null;

  const dayOf = (value: unknown): string | null => {
    const date = toDate(value);
    return date ? dayKeyOf(date, timeZone) : null;
  };
  const counted = starts.flatMap((value) => dayOf(value) ?? []);
  if (counted.length === 0) return null;

  const isOutside = (day: string) =>
    day < dates.startDate || day > dates.endDate;
  const outsideDays = [...new Set(counted.filter(isOutside))].sort();
  if (outsideDays.length === 0) return null;
  const outside = counted.filter(isOutside).length;

  // The attendee side numbers EVERY outside day on the agenda, in order, after
  // the event's own days. The days this notice quotes are only some of them
  // when other sessions are outside too, so the numbers come from all of them.
  const alsoCovered = alsoCover.flatMap((value) => dayOf(value) ?? []);
  const everyOutsideDay = [
    ...new Set([...counted, ...alsoCovered].filter(isOutside)),
  ].sort();

  const covered = [...counted, ...alsoCovered];
  const range: DayRange = {
    first: covered.reduce((a, b) => (b < a ? b : a)),
    last: covered.reduce((a, b) => (b > a ? b : a)),
  };
  const days = eventDayCount(range.first, range.last) ?? 0;

  const years = new Set(
    [
      dates.startDate,
      dates.endDate,
      range.first,
      range.last,
      ...outsideDays,
    ].map((key) => key.slice(0, 4)),
  );

  return {
    scheduled: counted.length,
    outside,
    outsideDays,
    dayNumbers: outsideDays.map(
      (day) => total + everyOutsideDay.indexOf(day) + 1,
    ),
    event: { first: dates.startDate, last: dates.endDate },
    offer:
      days > MAX_OFFERED_EVENT_DAYS
        ? { kind: "too-long", range, days }
        : { kind: "set", range },
    withYear: years.size > 1,
  };
}

/** "6 Oct", or "6 Oct 2027" when the year is needed. */
export function dayLabel(key: string, withYear: boolean): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    ...(withYear ? { year: "numeric" } : {}),
    timeZone: "UTC",
  }).format(new Date(`${key}T12:00:00Z`));
}

/** "6 Oct to 8 Oct", or "6 Oct" for one day. */
export function rangeLabel(range: DayRange, withYear: boolean): string {
  return range.first === range.last
    ? dayLabel(range.first, withYear)
    : `${dayLabel(range.first, withYear)} to ${dayLabel(range.last, withYear)}`;
}

/**
 * "Day 4", "Day 4 and Day 5", "Day 4 to Day 9", or for numbers with gaps
 * (other sessions outside the dates sit between) "Day 4, Day 6 and Day 9".
 */
export function dayNumbersPhrase(numbers: readonly number[]): string {
  const names = numbers.map((number) => `Day ${number}`);
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  const consecutive = numbers.every(
    (number, index) => index === 0 || number === numbers[index - 1] + 1,
  );
  if (consecutive) return `${names[0]} to ${names[names.length - 1]}`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * The first sentences of the notice.
 *
 * `subject` is where the sessions are: "the agenda" for those saved, "this
 * file" for those about to be imported, which also says what attendees would
 * see once they are.
 */
export function outsideDatesMessage(
  info: OutsideDates,
  subject: "agenda" | "file",
): string {
  // A file's count is of the rows that are ready to import, not of every row.
  const sessions =
    subject === "file"
      ? info.scheduled === 1
        ? "ready row"
        : "ready rows"
      : info.scheduled === 1
        ? "session"
        : "sessions";
  const where = subject === "file" ? " in this file" : "";
  const verb = info.outside === 1 ? "is" : "are";
  const days = info.outsideDays.length === 1 ? "a day" : "days";
  const eventDates = rangeLabel(info.event, info.withYear);
  const attendees =
    subject === "file" ? "Once imported, attendees see" : "Attendees see";
  const those = info.outsideDays.length === 1 ? "that day" : "those days";
  return (
    `${info.outside} of ${info.scheduled} ${sessions}${where} ${verb} on ${days} ` +
    `outside the event dates (${eventDates}). ` +
    `${attendees} ${those} as ${dayNumbersPhrase(info.dayNumbers)}.`
  );
}

/** "Set the event dates to 21 Oct to 22 Oct", "Set the event date to 21 Oct". */
export function setDatesLabel(range: DayRange, withYear: boolean): string {
  return `Set the event ${range.first === range.last ? "date" : "dates"} to ${rangeLabel(range, withYear)}`;
}

/** Whether the offered dates leave out a day the event has now. */
export function shortensEvent(event: DayRange, offered: DayRange): boolean {
  return offered.first > event.first || offered.last < event.last;
}

/** Why no button is offered for a range that is too long. */
export function tooLongMessage(
  offer: Extract<DatesOffer, { kind: "too-long" }>,
  withYear: boolean,
): string {
  return (
    `The sessions run from ${dayLabel(offer.range.first, withYear)} to ` +
    `${dayLabel(offer.range.last, withYear)}, which is ${offer.days} days. ` +
    `That is longer than the ${MAX_OFFERED_EVENT_DAYS} days an event can give ` +
    `titles to, and may be a typo in a date. Check the dates, or set the ` +
    `event dates yourself on the Settings tab.`
  );
}
